/**
 * Export Types - Clean Architecture Export System
 *
 * This file defines all types and interfaces for the export functionality
 * following clean architecture principles with separation of concerns.
 */

export type ExportFormat = 'csv' | 'excel';

export interface ExportColumn<T = unknown> {
  key: keyof T;
  title: string;
  formatter?: (_value: unknown, _row: T) => string;
  hidden?: boolean;
}

export interface ExportConfig<T = unknown> {
  columns: ExportColumn<T>[];
  filename: string;
  title?: string;
  description?: string;
}

export interface ExportData<T = unknown> {
  data: T[];
  config: ExportConfig<T>;
  format: ExportFormat;
}

export interface ExportResult {
  success: boolean;
  filename?: string;
  error?: string;
  recordCount: number;
}

export interface ExportOptions {
  includeHeaders?: boolean;
  includeTimestamp?: boolean;
  encoding?: 'utf-8' | 'utf-8-bom';
  delimiter?: string;
  /** Excel metadata author (e.g. from ClientProfile.theme.appTitle) */
  author?: string;
  // Excel-specific options
  sheetName?: string;
  autoColumnWidth?: boolean;
  freezeHeader?: boolean;
  minimizeHeaders?: boolean; // Make header row more compact
  includeTotals?: boolean; // Add totals row for numeric columns
  totalsLabel?: string; // Label for totals row (default: "الإجمالي")
}

export interface TableExportProps<T = unknown> {
  data: T[];
  config: ExportConfig<T>;
  format: ExportFormat;
  options?: ExportOptions;
}

export interface UseTableExportReturn {
  exportData: (_format: ExportFormat) => Promise<ExportResult>;
  isExporting: boolean;
  canExport: boolean;
  recordCount: number;
}

export interface ExportServiceInterface {
  exportToCSV<T>(props: TableExportProps<T>): Promise<ExportResult>;
  exportToExcel<T>(props: TableExportProps<T>): Promise<ExportResult>;
}

// Default export options
export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  includeHeaders: true,
  includeTimestamp: true,
  encoding: 'utf-8-bom',
  delimiter: ',',
  autoColumnWidth: true,
  freezeHeader: false,
  includeTotals: true, // Include totals row by default
  totalsLabel: 'الإجمالي'
};

// Export format configurations
export const EXPORT_FORMAT_CONFIG = {
  csv: {
    mimeType: 'text/csv;charset=utf-8;',
    extension: 'csv',
    label: 'CSV'
  },
  excel: {
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
    label: 'Excel'
  }
} as const;
